import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, X, BookOpen } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { extractError } from '../../lib/types.ts';
import { useToast } from '../ui/Toast.tsx';
import { TemplateCard } from './TemplateCard.tsx';
import type { TemplateItem } from './TemplateCard.tsx';

interface TemplateLibraryProps {
  /** If provided, cards show "Use template" and call this instead of navigating */
  onSelect?: (template: TemplateItem) => void;
  compact?: boolean;
}

export function TemplateLibrary({ onSelect, compact = false }: TemplateLibraryProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);

  const { data: templates = [], isLoading } = useQuery<TemplateItem[]>({
    queryKey: ['automation-templates'],
    queryFn: () => api.get('/api/v1/automation-templates').then((r) => r.data),
  });

  // Collect all unique tags across all templates
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    templates.forEach((t) => t.tags.forEach((tag) => tagSet.add(tag)));
    return [...tagSet].sort();
  }, [templates]);

  const filtered = useMemo(() => {
    let list = templates;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) => t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q),
      );
    }
    if (activeTags.length > 0) {
      list = list.filter((t) => activeTags.some((tag) => t.tags.includes(tag)));
    }
    return list;
  }, [templates, search, activeTags]);

  const toggleTag = (tag: string) =>
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const instantiateMutation = useMutation({
    mutationFn: ({ templateId, name }: { templateId: string; name?: string }) =>
      api.post(`/api/v1/automation-templates/${templateId}/instantiate`, { name }).then((r) => r.data as { id: string }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/automation-templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-templates'] });
      toast.info('Template deleted');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to delete template')),
  });

  async function handleUse(template: TemplateItem) {
    if (onSelect) {
      onSelect(template);
      return;
    }
    // Default: instantiate immediately and navigate to builder
    try {
      const def = await instantiateMutation.mutateAsync({ templateId: template.id });
      window.location.href = `/automations/${def.id}/build`;
    } catch (err) {
      toast.error(extractError(err, 'Failed to use template'));
    }
  }

  if (isLoading) {
    return (
      <div className={`${compact ? '' : 'p-4'}`}>
        <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-zinc-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filter bar */}
      <div className={`flex flex-wrap items-center gap-2 ${compact ? 'mb-3' : 'mb-4'}`}>
        <div className="relative flex-1 min-w-[160px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 text-xs border border-zinc-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 placeholder-zinc-400"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                activeTags.includes(tag)
                  ? 'bg-violet-600 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              {tag}
              {activeTags.includes(tag) && <X size={9} />}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
          <BookOpen size={28} className="mb-2 opacity-40" />
          <div className="text-sm">No templates found</div>
        </div>
      ) : (
        <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {filtered.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onUse={handleUse}
              onDelete={template.source === 'tenant' ? (t) => deleteMutation.mutate(t.id) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
